import { put, call, takeLatest, select, all, race, take } from "redux-saga/effects";
// redux-saga 0.16 has no delay *effect*; delay is a promise helper on the root
// module and is used through call().
import { delay } from "redux-saga";
import * as Api from "./workkardFormApi";
import {
  fetchWorkkardFieldsRequest,
  fetchWorkkardFieldsSuccess,
  fetchWorkkardFieldsError,
  fetchCarriersRequest,
  fetchCarriersSuccess,
  fetchCarriersError,
  createWorkkardRequest,
  createWorkkardAccepted,
  createWorkkardReady,
  createWorkkardSuccess,
  createWorkkardError,
  fetchWorkkardByIdRequest,
  fetchWorkkardByIdSuccess,
  fetchWorkkardByIdError,
  updateWorkkardRequest,
  updateWorkkardSuccess,
  updateWorkkardError,
} from "./workkardFormSlice";
import { WS_MESSAGE } from "../../../../common/actions/websocketActions";
import timezoneSaga from "./timezoneSaga";
import addressSaga from "./addressSaga";
import { toFormErrorPayload } from "../../utils/toFormErrorPayload";


function* fetchWorkkardFieldsSaga(action) {
  try {
    const { short_name, render_type } = action.payload;
    const response = yield call(Api.fetchWorkkardFields, { short_name, render_type });

    const fieldsFromResponse =
      response?.fields ?? response?.data ?? response?.Data ?? [];
    const fieldsList = Array.isArray(fieldsFromResponse)
      ? fieldsFromResponse
      : [];

    yield put(fetchWorkkardFieldsSuccess(fieldsList));
  } catch (error) {
    console.error("Fetch workkard fields error:", error);
    yield put(
      fetchWorkkardFieldsError(error?.message || "Failed to fetch fields"),
    );
  }
}

function* fetchCarriersSaga() {
  try {
    const response = yield call(Api.fetchCarriers);
    const carriersFromResponse =
      response?.results ?? response?.data ?? response?.Data ?? response ?? [];
    const carriersList = Array.isArray(carriersFromResponse)
      ? carriersFromResponse
      : [];
    yield put(fetchCarriersSuccess(carriersList));
  } catch (error) {
    console.error("Fetch carriers error:", error);
    yield put(fetchCarriersError(error?.message || "Failed to fetch carriers"));
  }
}

// How long to trust the socket before falling back to polling the read API.
const PROJECTION_SOCKET_TIMEOUT_MS = 8000;
// Poll settings for the fallback: covers a dead socket, a failed projection, or
// a reconnect that landed mid-flight.
const PROJECTION_POLL_INTERVAL_MS = 2000;
const PROJECTION_POLL_ATTEMPTS = 15; // ~30s

/**
 * Waits for the created claim to become readable.
 *
 * Creation is asynchronous: the POST only acknowledges validation, and the
 * claim is not fetchable until web-view has projected it. Redirecting on the
 * acknowledgement lands the user on an empty page, so the redirect waits for
 * either the `workkard.created` socket event carrying this request's id, or --
 * if that does not arrive -- for the read API to start answering.
 */
export function* awaitWorkkardProjection(requestId, workkardId) {
  if (requestId) {
    const { created } = yield race({
      created: call(takeMatchingCreation, requestId),
      timeout: call(delay, PROJECTION_SOCKET_TIMEOUT_MS),
    });
    if (created) {
      yield put(createWorkkardReady({ workkardId: created || workkardId }));
      return;
    }
  }

  if (!workkardId) {
    yield put(
      createWorkkardError(
        "The workkard was accepted but did not become available. Please refresh.",
      ),
    );
    return;
  }

  for (let attempt = 0; attempt < PROJECTION_POLL_ATTEMPTS; attempt += 1) {
    const projected = yield call(Api.fetchWorkkardById, workkardId);
    if (projected && !projected.error && projected.status !== 404) {
      yield put(createWorkkardReady({ workkardId }));
      return;
    }
    yield call(delay, PROJECTION_POLL_INTERVAL_MS);
  }

  yield put(
    createWorkkardError(
      "The workkard was created but is taking longer than expected to appear.",
    ),
  );
}

/**
 * Resolves with the created workkard id once a socket frame carries this
 * request's correlation id. Other tabs of the same browser receive the same
 * frame, but only the tab holding this requestId in its own saga acts on it.
 */
function* takeMatchingCreation(requestId) {
  while (true) {
    const { payload } = yield take(WS_MESSAGE);
    if (payload?.event !== "workkard.created") continue;
    if (payload?.requestId !== requestId) continue;
    return payload?.entity?.workkardId ?? null;
  }
}

// Exported for tests: the projection wait is the part worth driving directly.
export function* createWorkkardSaga(action) {
  try {
    const response = yield call(Api.createWorkkard, action.payload);
    if (response && (response.status === 400 || response.error)) {
      yield put(createWorkkardError(toFormErrorPayload(response, response.error || "Failed to create workkard")));
      return;
    }

    const requestId = response?.request_id ?? null;
    const workkardId = response?.workkard_id ?? null;

    // Without either identifier there is nothing to correlate or poll, so keep
    // the old behaviour and let the form navigate on the acknowledgement.
    if (!requestId && !workkardId) {
      yield put(createWorkkardSuccess(response));
      return;
    }

    yield put(createWorkkardAccepted({ requestId, workkardId }));
    yield call(awaitWorkkardProjection, requestId, workkardId);
  } catch (error) {
    console.error("Create workkard error:", error);
    yield put(createWorkkardError(toFormErrorPayload(error, "Failed to create workkard")));
  }
}

function* fetchWorkkardByIdSaga(action) {
  try {
    const { workkardId } = action.payload;
    const response = yield call(Api.fetchWorkkardById, workkardId);
    yield put(fetchWorkkardByIdSuccess(response));
  } catch (error) {
    console.error("Fetch workkard by ID error:", error);
    yield put(
      fetchWorkkardByIdError(error?.message || "Failed to fetch workkard"),
    );
  }
}

function* updateWorkkardSaga(action) {
  try {
    const response = yield call(Api.updateWorkkard, action.payload);
    if (response && (response.status === 400 || response.error)) {
      yield put(updateWorkkardError(toFormErrorPayload(response, response.error || "Failed to update workkard")));
      return;
    }
    yield put(updateWorkkardSuccess(response));
  } catch (error) {
    console.error("Update workkard error:", error);
    yield put(updateWorkkardError(toFormErrorPayload(error, "Failed to update workkard")));
  }
}

export default function* workkardFormSaga() {
  yield all([
    takeLatest(fetchWorkkardFieldsRequest.type, fetchWorkkardFieldsSaga),
    takeLatest(fetchCarriersRequest.type, fetchCarriersSaga),
    takeLatest(createWorkkardRequest.type, createWorkkardSaga),
    takeLatest(fetchWorkkardByIdRequest.type, fetchWorkkardByIdSaga),
    takeLatest(updateWorkkardRequest.type, updateWorkkardSaga),
    timezoneSaga(),
    addressSaga(),
  ]);
}
